/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import _debounce from 'lodash/debounce';
import _isEqual from 'lodash/isEqual';
import PropTypes from 'prop-types';

import ExpandableSection from 'components/expandableSectionV2';
import ZpButton from 'components/zpButton';
import { useInnerWidth } from 'hooks';
import { freezeBodyScroll, importStatic, unFreezeBodyScroll } from 'utils';

import { STATE_HISTORY_OPERATIONS } from '../../store/searchFilter';
import { updateFiltersState } from '../../store/searchFilter/actions';

import { FILTER_TYPES,mapFilterValues } from './filterUtils';
import ZpFilter from './index';

import zpFilterClasses from './zpFilter.module.css';

const propTypes = {
  filterGroup: PropTypes.string.isRequired,
  filters: PropTypes.arrayOf(PropTypes.object).isRequired,
  onApply: PropTypes.func,
  compact: PropTypes.bool,
  singleCompactFilter: PropTypes.bool,
  sideBarFilters: PropTypes.bool,
  isWrappedCarousel: PropTypes.bool,
  isMiniSidePanel: PropTypes.bool
};

const defaultProps = {
  filterGroup: '',
  filters: [],
  onApply: () => {},
  compact: true,
  singleCompactFilter: false,
  sideBarFilters: false,
  isWrappedCarousel: false,
  isMiniSidePanel: false
};

const FilterCarousel = ({
  compact,
  filters,
  onApply,
  filterGroup,
  singleCompactFilter,
  containerClass = '',
  sideBarFilters,
  isWrappedCarousel,
  isMiniSidePanel
}) => {
  const innerWidth = useInnerWidth(true);
  const MOBILE = 768;

  const ref = useRef();

  const dispatch = useDispatch();

  const { initialFiltersOpen, initialFiltersValue } = setInitialFilters();

  const [compactFilters, setCompactFilters] = useState(filters);

  const [filtersOpen, setFiltersOpen] = useState(initialFiltersOpen);

  const [filtersValue, setFiltersValue] = useState(initialFiltersValue);

  const [isCompact, setCompact] = useState(false);

  const [filtersActive, setFiltersActive] = useState(isFiltersActive(filtersValue));

  // Used to determine the param of a filter click when isCompact is true.
  const [filterClickSource, setFilterClickSource] = useState('');

  const stateHistory = useSelector(
    ({ searchFilter }) => searchFilter.stateHistory[filterGroup] || []
  );

  const stateHistoryLength = useSelector(
    ({ searchFilter }) => (searchFilter.stateHistory[filterGroup] || []).length
  );

  const stateHistoryChanged = useSelector(({ searchFilter }) => searchFilter.stateHistoryChanged);

  const jobsByCompany = useSelector(({ jobs }) => jobs.jobsByCompany);

  useEffect(() => {
    updateStateHistory(filtersValue, STATE_HISTORY_OPERATIONS.INIT);
  }, []);

  useEffect(() => {
    ref.current = filters;
  }, [filterGroup]);

  useEffect(() => {
    if (stateHistoryChanged) {
      onApply(mapFilterValues(stateHistory[stateHistoryLength - 1]));
      setFiltersValue(stateHistory[stateHistoryLength - 1]);
      setFiltersActive(isFiltersActive(stateHistory[stateHistoryLength - 1]));
      setCompactFilters(filters);
    }
  }, [stateHistoryLength]);

  useEffect(() => {
    const { initialFiltersValue } = setInitialFilters();
    setFiltersActive(isFiltersActive(initialFiltersValue));
  }, []);

  useEffect(() => {
    if (!_isEqual(compactFilters, filters)) {
      setCompactFilters(filters);
    }
  }, [filters]);

  function updateStateHistory(newState, operation) {
    dispatch(updateFiltersState({ filterGroup, newState, operation }));
  }

  useEffect(() => {
    if (
      filtersValue.location?.length &&
      jobsByCompany &&
      jobsByCompany.totalJobs === 0 &&
      jobsByCompany.pageNo === 0
    ) {
      filtersValue.location = [];
      updateStateHistory(filtersValue, STATE_HISTORY_OPERATIONS.APPEND);
    }
  }, [jobsByCompany]);

  // Scrolls the filter to the top. Mostly useful when mobile and isCompact.
  useEffect(() => {
    if (!filterClickSource) return;

    // Since we render filters multiple times, we have to find the right element.
    // It should be the last one.
    const nodeList = document.querySelectorAll(`[data-param="${filterClickSource}"]`);
    const filter = Array.from(nodeList).at(-1);

    if (filter) {
      filter.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [filterClickSource]);

  function setInitialFilters() {
    const initialFiltersOpen = {};
    const initialFiltersValue = {};

    filters.forEach(({ param, valueRange = [], preselectedItems = [] }) => {
      initialFiltersOpen[param] = false;
      const allValues = [
        ...valueRange,
        ...preselectedItems.map((item) => ({ ...item, enabled: true })),
      ];
      const enabledValues = allValues.filter(({ enabled }) => enabled);
      initialFiltersValue[param] = enabledValues;
    });
    return { initialFiltersOpen, initialFiltersValue };
  }

  function isFiltersActive(filtersValue) {
    if (!filtersValue) return false;
    for (const param of Object.keys(filtersValue).slice(singleCompactFilter ? 0 : 1)) {
      if (filtersValue[param] && filtersValue[param].length) {
        return true;
      }
    }
    return false;
  }

  const handleFilterClick = (param) => {
    // If this is a singleCompactFilter and on mobile
    // then send to handleCompactClick.
    if (singleCompactFilter && innerWidth <= MOBILE) {
      if(param !== "jobTitle"){
        setFilterClickSource(param);
        handleCompactClick(true);
      }
      return;
    }

    const filtersOpenCopy = { ...filtersOpen };
    for (const currentParam in filtersOpenCopy) {
      if (currentParam === param) {
        filtersOpenCopy[currentParam] = !filtersOpenCopy[param];
      } else {
        filtersOpenCopy[currentParam] = false;
      }
    }
    setFiltersOpen(filtersOpenCopy);
  };

  const handleFilterApply = (param, value) => {
    const filtersValueCopy = { ...filtersValue };
    filtersValueCopy[param] = !Array.isArray(value) ? [value] : value;
    updateStateHistory(filtersValueCopy, STATE_HISTORY_OPERATIONS.APPEND);
  };

  const zIndexUpdate = (add = true) => {
    const header = document.querySelector('header.header.header-new');
    const navigation = document.querySelector('#navigation');
    if (header) {
      if (add) {
        header.style.zIndex = 100;
      } else {
        header.style.zIndex = 99999;
      }
    }
    if (navigation) {
      if (add) {
        navigation.style.zIndex = 100;
      } else {
        navigation.style.zIndex = 99998;
      }
    }
  };

  const handleCompactClick = (isCompact) => {
    const filtersOpenCopy = { ...filtersOpen };
    Object.keys(filtersOpenCopy)
      .slice(singleCompactFilter ? 0 : 1)
      .forEach((param) => {
        filtersOpenCopy[param] = !filtersOpenCopy[param];
      });
    setFiltersOpen(filtersOpenCopy);
    setCompact(isCompact);
    if (isCompact) {
      zIndexUpdate();
      freezeBodyScroll();
    } else {
      zIndexUpdate(false);
      unFreezeBodyScroll();
    }
  };

  const handleCompactFilterSelect = (param, value, autoApply = false) => {
    setFiltersValue((prev) => {
      const values = {
        ...prev,
        [param]: !Array.isArray(value) ? [value] : value,
      };

      if (autoApply) handleCompactApply(values);

      return values;
    });
  };

  const handleCompactApply = (values = filtersValue) => {
    updateStateHistory(values, STATE_HISTORY_OPERATIONS.APPEND);
    let compactFiltersCopy = compactFilters.map((filter) => ({
      ...filter,
      preselectedItems: values?.[filter.param] || [],
    }));
    setCompactFilters(compactFiltersCopy);
    handleCompactClick(false);
    zIndexUpdate(false);
    unFreezeBodyScroll();
  };

  const handleCompactClear = () => {
    const filtersValueCopy = { ...filtersValue };
    let compactFiltersCopy = [...compactFilters];
    Object.keys(filtersValueCopy)
      .slice(singleCompactFilter ? 0 : 1)
      .forEach((param) => {
        filtersValueCopy[param] = [];
      });
    compactFiltersCopy = compactFiltersCopy.map((filter) => ({
      ...filter,
      preselectedItems: [],
    }));
    setCompactFilters(compactFiltersCopy);
    updateStateHistory(filtersValueCopy, STATE_HISTORY_OPERATIONS.APPEND);
    handleCompactClick(false);
    zIndexUpdate(false);
    unFreezeBodyScroll();
  };

  const getWrappedEnabledValues = (param) => {
    if (isWrappedCarousel) {
      return filtersValue?.[param] ?? [];
    } else if (stateHistory.length && stateHistory[stateHistory.length - 1]) {
      return stateHistory[stateHistory.length - 1]?.[param] ?? [];
    } else return [];
  };

  const getFilterKey = (filter, idx) => {
    const { buttonText, options, type, param, preselectedItems } = filter;

    const defaultKey = `${buttonText}-${idx}`;

    if (!isWrappedCarousel) return defaultKey;

    const value = filtersValue?.[param]?.[0]?.displayName ?? preselectedItems?.[0]?.displayName;

    if (type === FILTER_TYPES.AUTOSUGGEST && options?.duplicateForCompact) {
      return value;
    }

    return defaultKey;
  }

  return (
    <>
      <div
        className={classNames(
          zpFilterClasses['zp-filter-group'],
          'zp-filter-group',
          containerClass,
          'p-0'
        )}
      >
        <div
          className={classNames(
            zpFilterClasses['filter-carousel-container'],
            'filter-carousel-container'
          )}
        >
          <div
            className={classNames(
              !compact ? 'd-flex' : 'd-none',
              zpFilterClasses['filter-carousel'],
              { [zpFilterClasses['non-compact-carousel']]: !compact },
              { 'd-lg-flex': !sideBarFilters },
              { "flex-wrap": isWrappedCarousel },
              { [zpFilterClasses['mobile-buttons-filter']]: isWrappedCarousel }
            )}
          >
            {(!compact || (innerWidth > MOBILE && !sideBarFilters)) && filters.map((filter, idx) => (
              <ZpFilter
                key={getFilterKey(filter, idx)}
                alignRight={idx > 3}
                {...filter}
                filterGroup={filterGroup}
                filterOpen={filtersOpen[filter.param]}
                onFilterClick={handleFilterClick}
                onApply={handleFilterApply}
                onClear={handleFilterApply}
                filterEnabledValues={getWrappedEnabledValues(filter.param)}
                sideBarFilters={sideBarFilters}
                isMiniSidePanel={isMiniSidePanel}
              />
            ))}
          </div>

          <div className={classNames(
            (!compact && !isWrappedCarousel) ? 'd-none' : 'd-flex',
            "d-lg-none"
          )}>
            {!singleCompactFilter &&
              filters
                .slice(0, filters.length < 3 ? 3 : 1)
                .map((filter, idx) => (
                  <ZpFilter
                    key={getFilterKey(filter, idx)}
                    {...filter}
                    filterGroup={filterGroup}
                    filterOpen={filtersOpen[filter.param]}
                    onFilterClick={handleFilterClick}
                    onApply={handleFilterApply}
                    onClear={handleFilterApply}
                    filterEnabledValues={
                      stateHistory.length && stateHistory[stateHistory.length - 1]
                        ? stateHistory[stateHistory.length - 1][filter.param]
                        : []
                    }
                    sideBarFilters={sideBarFilters}
                  />
                ))}

            {filters.length > 2 && (
              <>
                {!isWrappedCarousel && !sideBarFilters && (
                <ZpButton
                  theme={filtersActive ? 'primary' : 'secondary'}
                  onClick={() => handleCompactClick(true)}
                  dropdownStatus="closed"
                  isLeftIcon={false}
                >
                  Filters
                </ZpButton>
                )}
                {isCompact && (
                  <div
                    className={classNames(
                      zpFilterClasses['compact-filter-wrapper'],
                      'd-flex flex-column'
                    )}
                  >
                    <div
                      className={classNames(
                        zpFilterClasses['filter-toolbar'],
                        'd-flex justify-content-between align-items-center d-lg-none filter-toolbar'
                      )}
                    >
                      <div className="filter-toolbar-start">
                        <img
                          src={importStatic('/ui-router/images/select-x-dark.svg')}
                          alt="cross-icon"
                          onClick={() => handleCompactClick(false)}
                        />
                      </div>
                      <div className="filter-toolbar-end" onClick={() => handleCompactClear()}>
                        Clear
                      </div>
                    </div>

                    <div className={zpFilterClasses['compact-filters']}>
                      {compactFilters.slice(singleCompactFilter ? 0 : 1).map((filter, idx) => (
                        <ZpFilter
                          key={`compact-${getFilterKey(filter, idx)}`}
                          {...filter}
                          filterGroup={filterGroup}
                          isCompact={isCompact}
                          filterOpen={filtersOpen[filter.param]}
                          filterEnabledValues={filtersValue[filter.param]}
                          onFilterSelect={handleCompactFilterSelect}
                          sideBarFilters={sideBarFilters}
                          isWrappedCarousel={isWrappedCarousel}
                        />
                      ))}
                    </div>

                    <div className={zpFilterClasses['filter-footer']}>
                      <div
                        className={zpFilterClasses['filter-footer-end']}
                        onClick={() => handleCompactApply()}
                      >
                        Apply
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {sideBarFilters && (
            <div className={classNames("z-p-20", zpFilterClasses['sidebar-filter-wrapper'])}>
              {filters.map((filter, idx) => {
                return (
                  <span key={`${filter.buttonText}-${idx}`}>
                    <ExpandableSection
                      sectionHeader={
                        <span className='font-weight-600'>{filter.buttonText}</span>
                      }
                      sectionBody={
                        <div>
                        <ZpFilter
                          key={idx}
                          {...filter}
                          filterGroup={filterGroup}
                          isCompact={true}
                          filterOpen={true}
                          filterEnabledValues={filtersValue[filter.param]}
                          onFilterSelect={_debounce((param, value) => {
                            handleCompactFilterSelect(param, value, true);
                          }, 1000)}
                          submitFilterOnChange={handleCompactApply}
                          sideBarFilters={sideBarFilters}
                          isWrappedCarousel={isWrappedCarousel}
                        />
                        </div>
                      }
                    />
                    {idx != filters.length - 1 && <section className="z-p-0 z-my-20 section-separator-large" />}
                  </span>
                )
              })}

            </div>
          )}
        </div>
      </div>
    </>
  );
};

FilterCarousel.propTypes = propTypes;
FilterCarousel.defaultProps = defaultProps;

export default React.memo(FilterCarousel);
